/**
 * User-facing error hints.
 *
 * Maps a failed API request to an actionable next step, printed after the raw
 * error message so the user gets guidance instead of only a status code + JSON
 * body. Returns null when there's nothing useful to add (the raw message stands
 * on its own). Wired into the single top-level catch in `index.ts`.
 */

import { ApiError } from './api-client.js';
import { isNetworkError } from './network-error.js';

export function friendlyHint(err: unknown): string | null {
  // A host-network failure (offline / DNS / connection reset) surfaces as a bare
  // `fetch failed` with no status — give the user something to act on (gh #2933).
  if (isNetworkError(err)) {
    return "Couldn't reach WayAI — check your network connection and try again.";
  }
  if (!(err instanceof ApiError)) return null;

  // Free-plan gate (e.g. `wayai publish`/`sync` — production is paid-only). The
  // backend throws a ValidationError carrying `code: 'FREE_PLAN_RESTRICTION'`;
  // match the body so the hint is legible regardless of the mapped HTTP status.
  if (err.body.includes('FREE_PLAN_RESTRICTION')) {
    return 'Publishing to production requires a paid plan. Upgrade your organization to publish or sync.';
  }

  const { status } = err;
  if (status === 401) return 'Your session may have expired. Run `wayai login` to re-authenticate.';
  if (status === 402) return 'Quota exceeded for your plan. Review usage or upgrade your plan.';
  if (status === 403) return 'Access denied. Your token may lack permission for this hub or resource.';
  if (status === 429) return 'Rate limited. Wait a moment and try again.';
  if (status >= 500) return 'WayAI server error — likely temporary. Try again shortly.';
  return null;
}
